import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Ban, UserCheck, Edit2, Save, X, Eye, ChevronDown, ChevronUp, Trash2, AlertTriangle, Shield, CheckCircle, XCircle, Filter, ChevronLeft, ChevronRight, ArrowUpDown, Mail, Users as UsersIcon, Send, LogIn } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

function timeAgo(dateStr) {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "< 1h";
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  // Warning modal state
  const [warnUser, setWarnUser] = useState(null); // { id, email, name }
  const [warnMessage, setWarnMessage] = useState("");
  const [warnHistory, setWarnHistory] = useState([]);
  const [warnBusy, setWarnBusy] = useState(false);
  const [filters, setFilters] = useState({ suspicious: false, same_ip: false, unverified: false });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadUsers = async () => {
    const params = new URLSearchParams();
    if (filters.suspicious) params.set("suspicious", "true");
    if (filters.same_ip) params.set("same_ip", "true");
    if (filters.unverified) params.set("unverified", "true");
    params.set("page", page);
    params.set("per_page", 50);
    params.set("sort_by", sortBy);
    params.set("sort_order", sortOrder);
    try {
      const { data } = await API.get(`/admin/users?${params.toString()}`);
      setUsers(data.users);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total_count || 0);
    } catch { toast.error("Failed to load users"); }
  };

  useEffect(() => { loadUsers(); }, [filters, page, sortBy, sortOrder]);

  const loadDetail = async (userId) => {
    if (expanded === userId) { setExpanded(null); setDetail(null); return; }
    try {
      const { data } = await API.get(`/admin/users/${userId}`);
      setDetail(data);
      setExpanded(userId);
    } catch { toast.error("Failed to load user details"); }
  };

  const ban = async (userId) => {
    await API.post(`/admin/users/${userId}/ban`);
    toast.success("User banned");
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_banned: true } : u));
  };

  const unban = async (userId) => {
    await API.post(`/admin/users/${userId}/unban`);
    toast.success("User unbanned");
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_banned: false } : u));
  };

  const markSuspicious = async (userId) => {
    await API.post(`/admin/users/${userId}/mark-suspicious`);
    toast.success("Marked suspicious");
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_suspicious: true } : u));
  };

  const unmarkSuspicious = async (userId) => {
    await API.post(`/admin/users/${userId}/unmark-suspicious`);
    toast.success("Unmarked suspicious");
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_suspicious: false } : u));
  };

  const forceVerify = async (userId) => {
    await API.post(`/admin/users/${userId}/force-verify`);
    toast.success("Email force-verified");
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, email_verified: true } : u));
  };

  const resendVerification = async (userId) => {
    try {
      const { data } = await API.post(`/admin/users/${userId}/resend-verification`);
      toast.success(data.message || "Verification email resent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user and ALL their data? This cannot be undone.")) return;
    try {
      await API.delete(`/admin/users/${userId}`);
      toast.success("User deleted");
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openWarnModal = async (u) => {
    setWarnUser({ id: u.id, email: u.email, name: u.name });
    setWarnMessage("");
    setWarnHistory([]);
    try {
      const { data } = await API.get(`/admin/users/${u.id}/warnings`);
      setWarnHistory(data.warnings || []);
    } catch { /* ignore */ }
  };

  const sendWarning = async () => {
    if (!warnMessage.trim()) { toast.error("Message required"); return; }
    setWarnBusy(true);
    try {
      await API.post(`/admin/users/${warnUser.id}/warn`, { message: warnMessage.trim() });
      toast.success("Warning sent");
      setWarnMessage("");
      const { data } = await API.get(`/admin/users/${warnUser.id}/warnings`);
      setWarnHistory(data.warnings || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setWarnBusy(false); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`Login as ${u.email}?\n\nA new tab will open with their account. This action is logged.`)) return;
    try {
      const { data } = await API.post(`/admin/users/${u.id}/impersonate`);
      const qs = new URLSearchParams({
        at: data.access_token || "",
        rt: data.refresh_token || "",
        email: data.email || u.email || "",
        name: data.name || u.name || "",
      }).toString();
      window.open(`/impersonate?${qs}`, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to impersonate");
    }
  };

  const resetIpRewards = async (ip) => {
    if (!window.confirm(`Reset all reward tracking for IP ${ip}?`)) return;
    try {
      await API.post("/admin/anti-abuse/reset-ip-rewards", { ip_address: ip });
      toast.success("IP rewards reset");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteIpAccounts = async (ip, keepUserId) => {
    if (!window.confirm(`Delete ALL other accounts from IP ${ip}? This keeps the selected user.`)) return;
    try {
      const { data } = await API.post("/admin/anti-abuse/delete-ip-accounts", { ip_address: ip, keep_user_id: keepUserId });
      toast.success(data.message);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (u) => {
    setEditing(u.id);
    setForm({ name: u.name, email: u.email, role: u.role, coins: u.coins, withdrawable_balance: u.withdrawable_balance, wallet_address: u.wallet_address || "", password: "" });
  };

  const saveUser = async (userId) => {
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      body.coins = parseFloat(body.coins);
      body.withdrawable_balance = parseFloat(body.withdrawable_balance);
      await API.put(`/admin/users/${userId}/edit`, body);
      toast.success("User updated");
      loadUsers();
      setEditing(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleFilter = (key) => { setPage(1); setFilters(prev => ({ ...prev, [key]: !prev[key] })); };
  const handleSort = (field) => {
    if (sortBy === field) { setSortOrder(prev => prev === "desc" ? "asc" : "desc"); }
    else { setSortBy(field); setSortOrder("desc"); }
    setPage(1);
  };

  return (
    <AdminLayout title="User Management">
      <div className="space-y-4" data-testid="admin-users">
        {/* Filters + Sort */}
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-[#64748B]" />
          <button onClick={() => toggleFilter("suspicious")} data-testid="filter-suspicious"
            className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${filters.suspicious ? "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30" : "text-[#64748B] border-[#2A3241] hover:text-[#F8FAFC]"}`}>
            Suspicious
          </button>
          <button onClick={() => toggleFilter("same_ip")} data-testid="filter-same-ip"
            className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${filters.same_ip ? "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" : "text-[#64748B] border-[#2A3241] hover:text-[#F8FAFC]"}`}>
            Same IP
          </button>
          <button onClick={() => toggleFilter("unverified")} data-testid="filter-unverified"
            className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${filters.unverified ? "bg-[#B026FF]/10 text-[#B026FF] border-[#B026FF]/30" : "text-[#64748B] border-[#2A3241] hover:text-[#F8FAFC]"}`}>
            Unverified
          </button>
          <div className="h-4 w-px bg-[#2A3241] mx-1" />
          <ArrowUpDown className="w-3.5 h-3.5 text-[#64748B]" />
          {[
            { field: "coins", label: "Coins", color: "#FFD600" },
            { field: "withdrawable_balance", label: "Balance", color: "#10B981" },
            { field: "deposit_balance", label: "Deposits", color: "#00F0FF" },
            { field: "created_at", label: "Newest", color: "#94A3B8" },
          ].map(s => (
            <button key={s.field} onClick={() => handleSort(s.field)} data-testid={`sort-${s.field}`}
              className={`px-2.5 py-1.5 text-xs rounded-lg border transition-all flex items-center gap-1 ${sortBy === s.field ? `border-opacity-30 text-opacity-100` : "text-[#64748B] border-[#2A3241] hover:text-[#F8FAFC]"}`}
              style={sortBy === s.field ? { borderColor: s.color + "4D", color: s.color, backgroundColor: s.color + "15" } : {}}>
              {s.label} {sortBy === s.field && (sortOrder === "desc" ? "↓" : "↑")}
            </button>
          ))}
          <span className="text-sm text-[#94A3B8] ml-auto">{totalCount} users</span>
        </div>

        {users.map(u => {
          const isEditing = editing === u.id;
          const isExpanded = expanded === u.id;

          return (
            <div key={u.id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden" data-testid={`user-row-${u.id}`}>
              {/* User Row */}
              <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-[#1C212E]/30" onClick={() => !isEditing && loadDetail(u.id)}>
                <div className="w-10 h-10 rounded-full bg-[#1C212E] flex items-center justify-center text-sm font-bold text-[#00F0FF] shrink-0">
                  {u.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-bold text-[#F8FAFC]">{u.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${u.role === "admin" ? "bg-[#FFD600]/10 text-[#FFD600]" : "bg-[#00F0FF]/10 text-[#00F0FF]"}`}>{u.role}</span>
                    {u.is_banned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EF4444]/10 text-[#EF4444] font-bold">BANNED</span>}
                    {u.is_suspicious && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B] font-bold" data-testid={`suspicious-badge-${u.id}`}>SUSPICIOUS</span>}
                    {!u.email_verified && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#B026FF]/10 text-[#B026FF] font-bold">UNVERIFIED</span>}
                    {u.same_ip_count > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B] font-bold" data-testid={`same-ip-badge-${u.id}`}>{u.same_ip_count} SAME IP</span>}
                  </div>
                  <p className="text-xs text-[#64748B] truncate">{u.email}</p>
                </div>
                {/* Compact Info */}
                <div className="hidden md:flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-mono text-[10px] text-[#64748B] truncate max-w-[100px]" title={u.registration_ip}>{u.registration_ip || "-"}</p>
                    <p className="text-[9px] text-[#475569]">IP</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[10px] text-[#64748B]" title={u.device_fingerprint}>{u.device_fingerprint ? u.device_fingerprint.slice(0, 8) + "..." : "-"}</p>
                    <p className="text-[9px] text-[#475569]">FP</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-[#FFD600]">{u.coins?.toFixed(0)}</p>
                    <p className="text-[9px] text-[#475569]">coins</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-[#B026FF]" data-testid={`user-deposit-${u.id}`}>${(u.deposit_balance ?? 0).toFixed(2)}</p>
                    <p className="text-[9px] text-[#475569]">deposit</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-[#10B981]" data-testid={`user-referrals-${u.id}`}>{u.referrals_count ?? 0}</p>
                    <p className="text-[9px] text-[#475569]">refs</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-[#94A3B8]">{timeAgo(u.created_at)}</p>
                    <p className="text-[9px] text-[#475569]">age</p>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                  <button onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`} className="p-1.5 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded-lg" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                  {u.is_suspicious
                    ? <button onClick={() => unmarkSuspicious(u.id)} className="p-1.5 text-[#10B981] hover:bg-[#10B981]/10 rounded-lg" title="Unmark suspicious"><Shield className="w-3.5 h-3.5" /></button>
                    : <button onClick={() => markSuspicious(u.id)} className="p-1.5 text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-lg" title="Mark suspicious"><AlertTriangle className="w-3.5 h-3.5" /></button>}
                  <button onClick={() => openWarnModal(u)} data-testid={`warn-user-${u.id}`} className="p-1.5 text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-lg" title="Send warning"><Send className="w-3.5 h-3.5" /></button>
                  {u.role !== "admin" && <button onClick={() => impersonate(u)} data-testid={`impersonate-user-${u.id}`} className="p-1.5 text-[#B026FF] hover:bg-[#B026FF]/10 rounded-lg" title="Login as this user"><LogIn className="w-3.5 h-3.5" /></button>}
                  {!u.email_verified && <button onClick={() => resendVerification(u.id)} data-testid={`resend-verification-${u.id}`} className="p-1.5 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-lg" title="Resend verification email"><Mail className="w-3.5 h-3.5" /></button>}
                  {!u.email_verified && <button onClick={() => forceVerify(u.id)} className="p-1.5 text-[#B026FF] hover:bg-[#B026FF]/10 rounded-lg" title="Force verify email"><CheckCircle className="w-3.5 h-3.5" /></button>}
                  {u.is_banned
                    ? <button onClick={() => unban(u.id)} className="p-1.5 text-[#10B981] hover:bg-[#10B981]/10 rounded-lg" title="Unban"><UserCheck className="w-3.5 h-3.5" /></button>
                    : <button onClick={() => ban(u.id)} className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg" title="Ban"><Ban className="w-3.5 h-3.5" /></button>}
                  <button onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`} className="p-1.5 text-[#64748B] hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-[#64748B] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#64748B] shrink-0" />}
              </div>

              {/* Edit Form */}
              {isEditing && (
                <div className="px-4 pb-4 pt-0 border-t border-[#00F0FF]/20 animate-slide-up">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                    <div><Label className="text-[#94A3B8] text-[10px]">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                    <div><Label className="text-[#94A3B8] text-[10px]">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                    <div><Label className="text-[#94A3B8] text-[10px]">New Password</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Leave blank to keep" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                    <div><Label className="text-[#94A3B8] text-[10px]">Role</Label><select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-sm"><option value="user">user</option><option value="admin">admin</option></select></div>
                    <div><Label className="text-[#94A3B8] text-[10px]">Coins</Label><Input type="number" value={form.coins} onChange={e => setForm({...form, coins: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-8 text-sm mt-1 font-mono" /></div>
                    <div><Label className="text-[#94A3B8] text-[10px]">Withdrawable</Label><Input type="number" step="0.01" value={form.withdrawable_balance} onChange={e => setForm({...form, withdrawable_balance: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#10B981] h-8 text-sm mt-1 font-mono" /></div>
                    <div className="sm:col-span-2"><Label className="text-[#94A3B8] text-[10px]">Wallet Address</Label><Input value={form.wallet_address} onChange={e => setForm({...form, wallet_address: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1 font-mono" /></div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => saveUser(u.id)} data-testid={`save-user-${u.id}`} className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg"><Save className="w-3 h-3 inline mr-1" />Save</button>
                    <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs text-[#94A3B8] border border-[#2A3241] rounded-lg"><X className="w-3 h-3 inline mr-1" />Cancel</button>
                  </div>
                </div>
              )}

              {/* Expanded Detail */}
              {isExpanded && detail && !isEditing && (
                <div className="px-4 pb-4 pt-0 border-t border-[#2A3241]/30 animate-slide-up space-y-3">
                  {/* Anti-Abuse Info */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    <InfoCard label="Registration IP" value={detail.user?.registration_ip || "-"} mono />
                    <InfoCard label="Last Login IP" value={detail.user?.last_login_ip || "-"} mono />
                    <InfoCard label="Fingerprint" value={detail.user?.device_fingerprint ? detail.user.device_fingerprint.slice(0, 16) + "..." : "-"} mono />
                    <InfoCard label="Account Age" value={timeAgo(detail.user?.created_at)} />
                  </div>

                  {/* Same IP Accounts */}
                  {detail.same_ip_accounts?.length > 0 && (
                    <div className="bg-[#0B0E14] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-bold text-[#EF4444] uppercase tracking-wider">Same IP Accounts ({detail.same_ip_accounts.length})</p>
                        <div className="flex gap-1">
                          <button onClick={() => resetIpRewards(detail.user?.registration_ip)} className="text-[10px] px-2 py-1 rounded bg-[#B026FF]/10 text-[#B026FF] hover:bg-[#B026FF]/20" data-testid="reset-ip-rewards-btn">Reset IP Rewards</button>
                          <button onClick={() => deleteIpAccounts(detail.user?.registration_ip, u.id)} className="text-[10px] px-2 py-1 rounded bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20" data-testid="delete-ip-accounts-btn">Delete Others</button>
                        </div>
                      </div>
                      {detail.same_ip_accounts.map((acc, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#2A3241]/20 last:border-0">
                          <div>
                            <span className="text-xs text-[#F8FAFC]">{acc.name}</span>
                            <span className="text-[10px] text-[#64748B] ml-2">{acc.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {acc.is_suspicious && <span className="text-[9px] text-[#F59E0B]">SUSPICIOUS</span>}
                            {acc.is_banned && <span className="text-[9px] text-[#EF4444]">BANNED</span>}
                            <span className="text-[10px] text-[#64748B]">{acc.coins?.toFixed(0)}c</span>
                            <span className="text-[10px] text-[#475569]">{timeAgo(acc.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Same Fingerprint Accounts */}
                  {detail.same_fp_accounts?.length > 0 && (
                    <div className="bg-[#0B0E14] rounded-lg p-3">
                      <p className="text-[10px] font-bold text-[#B026FF] uppercase tracking-wider mb-2">Same Fingerprint Accounts ({detail.same_fp_accounts.length})</p>
                      {detail.same_fp_accounts.map((acc, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#2A3241]/20 last:border-0">
                          <div>
                            <span className="text-xs text-[#F8FAFC]">{acc.name}</span>
                            <span className="text-[10px] text-[#64748B] ml-2">{acc.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {acc.is_suspicious && <span className="text-[9px] text-[#F59E0B]">SUSPICIOUS</span>}
                            <span className="text-[10px] text-[#64748B]">{acc.coins?.toFixed(0)}c</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* IP Reward History */}
                  {detail.ip_reward_history?.length > 0 && (
                    <div className="bg-[#0B0E14] rounded-lg p-3">
                      <p className="text-[10px] font-bold text-[#FFD600] uppercase tracking-wider mb-2">IP Reward History</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {detail.ip_reward_history.slice(0, 8).map((r, i) => (
                          <div key={i} className="bg-[#131722] rounded p-2 text-center">
                            <p className="text-[10px] text-[#64748B]">{r.date}</p>
                            <p className="text-sm font-mono text-[#FFD600]">{r.total_rewards?.toFixed(1)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Login IPs */}
                  {detail.login_ips?.length > 0 && (
                    <div className="bg-[#0B0E14] rounded-lg p-3">
                      <p className="text-[10px] font-bold text-[#00F0FF] uppercase tracking-wider mb-2">Login IP History ({detail.login_ips.length})</p>
                      <div className="flex flex-wrap gap-1.5">
                        {detail.login_ips.map((ip, i) => (
                          <span key={i} className="text-[10px] font-mono px-2 py-1 rounded bg-[#131722] text-[#94A3B8] border border-[#2A3241]/30">{ip}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Workers & Transactions */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InfoCard label="Workers" value={`${detail.workers?.length || 0} workers`} />
                    <InfoCard label="Recent Transactions" value={`${detail.transactions?.length || 0} transactions`} />
                  </div>

                  {/* Deposit Proofs */}
                  {detail.deposits?.filter(d => d.proof || d.method === "coin_purchase").length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-[#FFD600] uppercase tracking-wider mb-2">Deposit Proofs</p>
                      {detail.deposits.filter(d => d.proof || d.method === "coin_purchase").slice(0, 5).map((d, i) => (
                        <div key={i} className="bg-[#0B0E14] rounded-lg p-3 mb-1 border border-[#2A3241]/30">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-[#F8FAFC]">${d.amount} {d.currency || ""} {d.package_name ? `(${d.package_name})` : ""}</span>
                            <span className="text-[10px]" style={{color: STATUS_COLOR[d.status]}}>{d.status}</span>
                          </div>
                          {d.proof && <p className="text-xs font-mono text-[#94A3B8] break-all">{d.proof}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4" data-testid="pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="pagination-prev"
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold uppercase rounded-lg border border-[#2A3241] text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1C212E] disabled:opacity-30 disabled:cursor-not-allowed transition-all">
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <button key={p} onClick={() => setPage(p)} data-testid={`pagination-page-${p}`}
                    className={`w-9 h-9 text-xs font-bold rounded-lg transition-all ${p === page ? "bg-[#FFD600]/10 text-[#FFD600] border border-[#FFD600]/30" : "text-[#64748B] hover:text-[#F8FAFC] hover:bg-[#1C212E] border border-[#2A3241]"}`}>
                    {p}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="pagination-next"
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold uppercase rounded-lg border border-[#2A3241] text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1C212E] disabled:opacity-30 disabled:cursor-not-allowed transition-all">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Warning Modal */}
      {warnUser && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setWarnUser(null)} data-testid="admin-warn-modal">
          <div className="bg-[#131722] border border-[#F59E0B]/40 rounded-xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#F8FAFC]">Send Warning</h3>
                  <p className="text-xs text-[#94A3B8]">To: <span className="text-[#F59E0B]">{warnUser.email}</span></p>
                </div>
              </div>
              <button onClick={() => setWarnUser(null)} className="text-[#64748B] hover:text-[#F8FAFC]"><X className="w-5 h-5" /></button>
            </div>

            <div className="mb-4">
              <Label className="text-[#94A3B8] text-xs mb-1 block">Message</Label>
              <textarea value={warnMessage} onChange={(e) => setWarnMessage(e.target.value)} rows={4} placeholder="Explain the suspicious activity / behavior detected..."
                className="w-full bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-lg p-3 text-sm resize-none focus:border-[#F59E0B]/50 focus:outline-none" data-testid="warn-message-input" />
              <button onClick={sendWarning} disabled={warnBusy || !warnMessage.trim()} data-testid="send-warn-btn"
                className="mt-2 w-full py-2.5 text-xs font-bold uppercase tracking-widest bg-[#F59E0B] text-[#0B0E14] rounded-lg hover:bg-[#F59E0B]/90 disabled:opacity-50 flex items-center justify-center gap-2">
                <Send className="w-3.5 h-3.5" /> {warnBusy ? "Sending..." : "Send Warning"}
              </button>
            </div>

            {warnHistory.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">Warning History</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {warnHistory.map((w) => (
                    <div key={w.warning_id} className="bg-[#0B0E14] border border-[#2A3241]/40 rounded-lg p-3" data-testid={`warn-history-${w.warning_id}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${w.dismissed ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>
                          {w.dismissed ? "✓ Read" : "⏳ Unread"}
                        </span>
                        <span className="text-[10px] text-[#64748B]">{new Date(w.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-[#F8FAFC] whitespace-pre-wrap break-words">{w.message}</p>
                      {w.dismissed && w.dismissed_at && (
                        <p className="text-[10px] text-[#64748B] mt-1">Read at: {new Date(w.dismissed_at).toLocaleString()}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function InfoCard({ label, value, mono }) {
  return (
    <div className="bg-[#0B0E14] rounded-lg p-3">
      <p className="text-[10px] text-[#64748B] uppercase mb-1">{label}</p>
      <p className={`text-sm text-[#F8FAFC] ${mono ? "font-mono" : ""} break-all`}>{value}</p>
    </div>
  );
}

const STATUS_COLOR = { pending: "#F59E0B", approved: "#10B981", rejected: "#EF4444" };
